import { useState, useEffect } from "react";
import { Plus, Search, Building, Trash2, Pencil } from "lucide-react";
import { clientAPI } from "../../services/api";

export default function SuperClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", status: "Active" });

  const load = () => {
    setLoading(true);
    clientAPI
      .list()
      .then((r) =>
        setClients(Array.isArray(r.data) ? r.data : r.data?.data || []),
      )
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.vendor_code || "").toLowerCase().includes(search.toLowerCase()),
  );

  const save = async () => {
    if (!form.name) return alert("Client name required");
    setSaving(true);
    try {
      if (editingId) {
        await clientAPI.update(editingId, form);
      } else {
        await clientAPI.create(form);
      }
      closeForm();
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (c) => {
    setForm({ name: c.name, address: c.address || "", status: c.status });
    setEditingId(c.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setForm({ name: "", address: "", status: "Active" });
    setEditingId(null);
    setShowForm(false);
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === "Active" ? "Inactive" : "Active";
    setTogglingId(c.id);
    // Optimistic update so the badge/button flip instantly
    setClients((prev) =>
      prev.map((x) => (x.id === c.id ? { ...x, status: newStatus } : x)),
    );
    try {
      await clientAPI.update(c.id, { ...c, status: newStatus });
    } catch (e) {
      alert(e.message);
      // Revert on failure
      setClients((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, status: c.status } : x)),
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`))
      return;
    setDeletingId(c.id);
    try {
      await clientAPI.delete(c.id);
      setClients((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      alert(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (showForm)
    return (
      <div style={{ padding: 28, maxWidth: 1000 }}>
        <button
          onClick={closeForm}
          style={{
            background: "none",
            border: "none",
            color: "var(--text2)",
            cursor: "pointer",
            fontSize: 13,
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ← Back to Clients
        </button>

        <div
          className="form-header"
          style={{
            background: "linear-gradient(135deg,#1a56db,#3b82f6)",
            borderRadius: 12,
            padding: "20px 24px",
            marginBottom: 24,
            color: "#fff",
            margin: "0 auto",
            maxWidth: 800,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {editingId ? "Edit Client" : "Add New Client"}
              </h2>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.85 }}>
                Client Company Information
              </p>
            </div>
          </div>
        </div>

        <div
          className="form-container"
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 28,
            margin: "0 auto",
            maxWidth: 800,
          }}
        >
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Client Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ABC Corporation"
              autoFocus
            />
          </div>

          {/* <div className="form-group" style={{ marginBottom:16 }}>
          <label>Vendor Code</label>
          <input value={form.vendor_code} onChange={e => setForm({...form,vendor_code:e.target.value})} placeholder="VEN001" />
        </div> */}

          <div className="form-group" style={{ marginBottom: 20 }}>
            <label>Address</label>
            <textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Full address of the client company"
              rows={3}
              style={{ resize: "vertical" }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 24 }}>
            <label>Status</label>
            <div style={{ display: "flex", gap: 20, marginTop: 8 }}>
              {["Active", "Inactive"].map((s) => (
                <label
                  key={s}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="radio"
                    name="status"
                    value={s}
                    checked={form.status === s}
                    onChange={() => setForm({ ...form, status: s })}
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="btn-primary"
              onClick={save}
              disabled={saving}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              ✓{" "}
              {saving
                ? editingId
                  ? "Saving..."
                  : "Creating..."
                : editingId
                  ? "Save Changes"
                  : "Create Client"}
            </button>
            <button className="btn-secondary" onClick={closeForm}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="page-container" style={{ padding: 28 }}>
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Clients</h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Manage all client companies
          </p>
        </div>
      </div>

      <div style={{ position: "relative", maxWidth: 320, marginBottom: 20 }}>
        <Search
          size={13}
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--text3)",
          }}
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients..."
          style={{ paddingLeft: 30 }}
        />
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Client Name</th>
                {/* <th>Vendor Code</th> */}
                <th>Address</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: "rgba(124,92,252,0.15)",
                          border: "1px solid rgba(124,92,252,0.3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Building size={13} color="var(--accent2)" />
                      </div>
                      <span style={{ fontWeight: 500 }}>{c.name}</span>
                    </div>
                  </td>
                  {/* <td className="mono text2">{c.vendor_code || '—'}</td> */}
                  <td className="mono">{c.address || "—"}</td>
                  <td>
                    <span
                      className={`badge ${c.status === "Active" ? "badge-success" : "badge-danger"}`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 14 }}
                    >
                      {/* Edit icon button */}
                      <button
                        onClick={() => openEdit(c)}
                        title="Edit client"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          cursor: "pointer",
                          border: "none",
                          background: "rgba(255,159,67,0.18)",
                          color: "#ff9f43",
                          flexShrink: 0,
                        }}
                      >
                        <Pencil size={14} />
                      </button>

                      {/* iOS-style toggle switch */}
                      <div
                        onClick={() => togglingId !== c.id && toggleStatus(c)}
                        title={
                          c.status === "Active"
                            ? "Click to deactivate"
                            : "Click to activate"
                        }
                        style={{
                          width: 40,
                          height: 22,
                          borderRadius: 999,
                          position: "relative",
                          cursor:
                            togglingId === c.id ? "not-allowed" : "pointer",
                          background:
                            c.status === "Active"
                              ? "var(--success)"
                              : "var(--border)",
                          transition: "background 0.2s ease",
                          opacity: togglingId === c.id ? 0.6 : 1,
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 2,
                            left: c.status === "Active" ? 20 : 2,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            background: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                            transition: "left 0.2s ease",
                          }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
